import {publishOptimisticFields} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {pinIssueMutation} from './__generated__/pinIssueMutation.graphql'

export function commitPinIssueMutation({
  environment,
  input,
  repositoryId,
  currentPinnedCount,
  onCompleted,
  onError,
}: {
  environment: Environment
  input: {issueId: string}
  repositoryId?: string
  currentPinnedCount?: number
  onCompleted?: () => void
  onError?: (error: Error) => void
}) {
  const optimisticResponse =
    repositoryId !== undefined && currentPinnedCount !== undefined
      ? {
          pinIssue: {
            issue: {
              id: input.issueId,
              isPinned: true,
              repository: {
                id: repositoryId,
                pinnedIssues: {
                  totalCount: currentPinnedCount + 1,
                },
              },
            },
          },
        }
      : undefined

  // Only the issue's own field: the repository's `pinnedIssues.totalCount` belongs to a different
  // entity, so it would be a second operation targeting `repositoryId` rather than a nested write
  // through the issue. No consumer needs that count yet.
  const optimistic = publishOptimisticFields(input.issueId, {isPinned: true})

  let disposable
  try {
    disposable = commitMutation<pinIssueMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation pinIssueMutation($input: PinIssueInput!) @raw_response_type {
          pinIssue(input: $input) {
            issue {
              isPinned
              repository {
                pinnedIssues(first: 3) {
                  totalCount
                }
              }
            }
          }
        }
      `,
      variables: {
        input,
      },
      optimisticResponse,
      onCompleted: (response, errors) => {
        const isPinned = response.pinIssue?.issue?.isPinned
        if (errors?.length || isPinned == null) {
          const error = new Error(errors?.[0]?.message ?? 'Failed to pin issue')
          optimistic.failed(error)
          onError?.(error)
          return
        }
        optimistic.completed({isPinned})
        onCompleted?.()
      },
      onUnsubscribe: () => optimistic.failed(new Error('Pinning the issue was cancelled')),
      onError: error => {
        optimistic.failed(error)
        onError?.(error)
      },
    })
  } catch (error) {
    optimistic.failed(error instanceof Error ? error : new Error('Failed to pin issue'))
    throw error
  }

  return disposable
}
